import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    downloadExcel,
    getSubmissions,
    updateSubmissionStatus,
} from '../services/api.service';
import { useAuth } from '../services/auth.service';
import moment from 'moment';
import {
    FaChevronLeft,
    FaChevronRight,
    FaTimes,
} from 'react-icons/fa';
import StatusDropdown from '../components/status-dropdown';
import Loading from '../components/loading';

const DEFAULT_SORT_CONFIG = {
    key: 'createdAt',
    direction: 'descending',
};

const STATUSES = [
    'created',
    'paid',
    'in-progress',
    'completed',
    'rejected',
    'refunded',
    'cancelled',
];

const STATUS_LABELS = {
    created: 'Créé',
    paid: 'Payée',
    'in-progress': 'En cours',
    completed: 'Complété',
    rejected: 'Rejeté',
    refunded: 'Remboursé',
    cancelled: 'Annulé',
};

function getServiceLabel(service) {
    return (
        service?.title?.fr ||
        service?.title?.en ||
        service?.title?.de ||
        'Service'
    ).trim();
}

function getLastHandler(submission) {
    const history = Array.isArray(submission?.history)
        ? submission.history
        : [];

    return [...history]
        .reverse()
        .find(item => item?.status && item?.firstName && item?.lastName);
}

function getSortValue(submission, key) {
    switch (key) {
        case 'lastname':
            return submission?.customer?.lastname || '';
        case 'email':
            return submission?.customer?.email || '';
        case 'companyName':
            return submission?.customer?.companyName || submission?.subdomain || '';
        case 'transactionNumber':
            return submission?.dossierNumber || submission?.transactionNumber || '';
        case 'createdAt':
            return new Date(submission?.createdAt || 0).getTime();
        default:
            return submission?.[key] || '';
    }
}

function DashboardPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Keep the state received from the submission page stable for the lifetime
    // of this dashboard render.
    const restoredDashboardStateRef = useRef(
        location.state?.dashboardState || {}
    );
    const restoredDashboardState = restoredDashboardStateRef.current;
    const scrollRestoredRef = useRef(false);

    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);

    const [sortConfig, setSortConfig] = useState(
        restoredDashboardState.sortConfig || DEFAULT_SORT_CONFIG
    );
    const [search, setSearch] = useState(
        restoredDashboardState.search || ''
    );
    const [filterSociety, setFilterSociety] = useState(
        restoredDashboardState.filterSociety || ''
    );
    const [filterStatus, setFilterStatus] = useState(
        restoredDashboardState.filterStatus || ''
    );
    const [filterService, setFilterService] = useState(
        restoredDashboardState.filterService || ''
    );
    const [filterHandledBy, setFilterHandledBy] = useState(
        restoredDashboardState.filterHandledBy || ''
    );
    const [page, setPage] = useState(
        Number(restoredDashboardState.page) || 1
    );
    const [pageSize, setPageSize] = useState(
        Number(restoredDashboardState.pageSize) || 25
    );

    useEffect(() => {
        let isMounted = true;

        const loadSubmissions = async () => {
            try {
                setLoading(true);
                const data = await getSubmissions();

                if (isMounted) {
                    setSubmissions(Array.isArray(data) ? data : []);
                }
            } catch (error) {
                console.error('Error loading submissions:', error);

                if (isMounted) {
                    setSubmissions([]);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadSubmissions();

        return () => {
            isMounted = false;
        };
    }, []);

    const filtered = useMemo(() => {
        let result = [...submissions];

        if (search) {
            const lowerSearch = search.toLowerCase().trim();

            result = result.filter(submission => {
                const customer = submission?.customer || {};
                const searchableValues = [
                    customer.firstname,
                    customer.lastname,
                    customer.email,
                    customer.companyName,
                    customer.subdomain,
                    submission?.subdomain,
                ];

                return searchableValues.some(value =>
                    String(value || '')
                        .toLowerCase()
                        .includes(lowerSearch)
                );
            });
        }

        if (filterSociety) {
            const normalizedSociety = filterSociety.toLowerCase();

            result = result.filter(submission =>
                String(submission?.subdomain || '').toLowerCase() ===
                normalizedSociety
            );
        }

        if (filterStatus) {
            result = result.filter(
                submission => submission?.status === filterStatus
            );
        }

        if (filterHandledBy) {
            if (filterHandledBy === 'none') {
                result = result.filter(submission => {
                    const history = Array.isArray(submission?.history)
                        ? submission.history
                        : [];

                    return !history.some(
                        item => item?.firstName && item?.lastName
                    );
                });
            } else {
                result = result.filter(submission => {
                    const handler = getLastHandler(submission);

                    return Boolean(
                        handler &&
                        `${handler.firstName} ${handler.lastName}` ===
                        filterHandledBy
                    );
                });
            }
        }

        if (filterService) {
            result = result.filter(submission =>
                (submission?.services || []).some(
                    service => getServiceLabel(service) === filterService
                )
            );
        }

        return result;
    }, [
        submissions,
        search,
        filterSociety,
        filterStatus,
        filterHandledBy,
        filterService,
    ]);

    const sorted = useMemo(() => {
        return [...filtered].sort((first, second) => {
            const firstValue = getSortValue(first, sortConfig.key);
            const secondValue = getSortValue(second, sortConfig.key);

            let comparison = 0;

            if (
                typeof firstValue === 'number' &&
                typeof secondValue === 'number'
            ) {
                comparison = firstValue - secondValue;
            } else {
                comparison = String(firstValue).localeCompare(
                    String(secondValue),
                    'fr',
                    {
                        numeric: true,
                        sensitivity: 'base',
                    }
                );
            }

            return sortConfig.direction === 'ascending'
                ? comparison
                : -comparison;
        });
    }, [filtered, sortConfig]);

    const maxPage = Math.max(
        1,
        Math.ceil(filtered.length / pageSize)
    );

    const paginated = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return sorted.slice(startIndex, startIndex + pageSize);
    }, [sorted, page, pageSize]);

    // If a status/filter update removes enough rows to invalidate the current
    // page, stay on the nearest existing page instead of jumping to page 1.
    useEffect(() => {
        setPage(currentPage => Math.min(currentPage, maxPage));
    }, [maxPage]);

    // Restore the exact vertical position after the list and restored page
    // have been rendered.
    useEffect(() => {
        const restoredScrollY = Number(restoredDashboardState.scrollY);

        if (
            loading ||
            scrollRestoredRef.current ||
            !restoredDashboardState.hasSavedState ||
            !Number.isFinite(restoredScrollY)
        ) {
            return undefined;
        }

        let secondFrameId;

        const firstFrameId = requestAnimationFrame(() => {
            secondFrameId = requestAnimationFrame(() => {
                window.scrollTo({
                    top: restoredScrollY,
                    behavior: 'auto',
                });

                scrollRestoredRef.current = true;
            });
        });

        return () => {
            cancelAnimationFrame(firstFrameId);

            if (secondFrameId !== undefined) {
                cancelAnimationFrame(secondFrameId);
            }
        };
    }, [
        loading,
        restoredDashboardState.hasSavedState,
        restoredDashboardState.scrollY,
    ]);

    const sortBy = key => {
        setSortConfig(currentConfig => ({
            key,
            direction:
                currentConfig.key === key &&
                    currentConfig.direction === 'ascending'
                    ? 'descending'
                    : 'ascending',
        }));
    };

    const updateStatus = async (id, newStatus, comment) => {
        try {
            await updateSubmissionStatus(id, newStatus, comment, {
                firstname: user.firstname,
                lastname: user.lastname,
            });

            const updatedSubmissions = await getSubmissions();
            setSubmissions(
                Array.isArray(updatedSubmissions)
                    ? updatedSubmissions
                    : []
            );
        } catch (error) {
            console.error('Error updating submission status:', error);
        }
    };

    const openSubmission = submissionId => {
        navigate(`/${submissionId}`, {
            state: {
                dashboardState: {
                    hasSavedState: true,
                    page,
                    pageSize,
                    search,
                    filterSociety,
                    filterStatus,
                    filterService,
                    filterHandledBy,
                    sortConfig,
                    scrollY: window.scrollY,
                },
            },
        });
    };

    const resetFilters = () => {
        setSearch('');
        setFilterSociety('');
        setFilterStatus('');
        setFilterHandledBy('');
        setFilterService('');
        setPage(1);
    };

    const serviceOptions = useMemo(() => {
        const counts = new Map();

        submissions.forEach(submission => {
            (submission?.services || []).forEach(service => {
                const label = getServiceLabel(service);

                if (!label) {
                    return;
                }

                counts.set(label, (counts.get(label) || 0) + 1);
            });
        });

        return Array.from(counts.entries())
            .sort((first, second) =>
                first[0].localeCompare(second[0], 'fr', {
                    sensitivity: 'base',
                })
            )
            .map(([label, count]) => ({ label, count }));
    }, [submissions]);

    const societies = useMemo(() => {
        return [
            ...new Set(
                submissions
                    .map(submission => submission?.subdomain)
                    .filter(Boolean)
            ),
        ].sort((first, second) =>
            first.localeCompare(second, 'fr', {
                sensitivity: 'base',
            })
        );
    }, [submissions]);

    const handledByList = useMemo(() => {
        const handlers = submissions
            .map(submission => {
                const handler = getLastHandler(submission);

                return handler
                    ? `${handler.firstName} ${handler.lastName}`
                    : null;
            })
            .filter(Boolean);

        return ['none', ...new Set(handlers)];
    }, [submissions]);

    const download = () => downloadExcel();

    return (
        <div className="max-w-full p-6 space-y-4">
            {/* Filters */}
            <div className="h-fit mb-4">
                <div className="flex justify-between items-center">
                    <div className="flex flex-wrap items-center gap-4 mb-8">
                        <div className="relative w-full sm:w-96 h-fit">
                            <input
                                value={search}
                                onChange={event => {
                                    setSearch(event.target.value);
                                    setPage(1);
                                }}
                                placeholder="Rechercher prénom, nom, email, société"
                                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearch('');
                                        setPage(1);
                                    }}
                                    className="absolute right-2 top-3 text-gray-500"
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={download}
                        className="bg-primary text-white px-3 py-2 rounded hover:bg-blue-700"
                    >
                        Télécharger Excel
                    </button>
                </div>

                <div className="flex flex-row gap-6">
                    <div className="relative w-full sm:w-96 h-fit">
                        <select
                            value={filterService}
                            onChange={event => {
                                setFilterService(event.target.value);
                                setPage(1);
                            }}
                            className="appearance-none cursor-pointer bg-white w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tous les services</option>
                            {serviceOptions.map(option => (
                                <option
                                    key={option.label}
                                    value={option.label}
                                >
                                    {option.label} ({option.count})
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                            <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>
                    </div>

                    <div className="relative w-full sm:w-60 h-fit">
                        <select
                            value={filterSociety}
                            onChange={event => {
                                setFilterSociety(event.target.value);
                                setPage(1);
                            }}
                            className={`appearance-none cursor-pointer bg-white w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${filterSociety !== '' ? 'capitalize' : ''
                                }`}
                        >
                            <option value="" className="normal-case">
                                Toutes les sociétés
                            </option>
                            {societies.map(society => (
                                <option
                                    key={society}
                                    value={society}
                                    className="capitalize"
                                >
                                    {society}
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                            <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>
                    </div>

                    <div className="relative w-full sm:w-60 h-fit">
                        <select
                            value={filterStatus}
                            onChange={event => {
                                setFilterStatus(event.target.value);
                                setPage(1);
                            }}
                            className="appearance-none cursor-pointer bg-white w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tous les statuts</option>
                            {STATUSES.map(status => (
                                <option key={status} value={status}>
                                    {STATUS_LABELS[status]}
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                            <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>
                    </div>

                    <div className="relative w-full sm:w-60 h-fit">
                        <select
                            value={filterHandledBy}
                            onChange={event => {
                                setFilterHandledBy(event.target.value);
                                setPage(1);
                            }}
                            className="appearance-none cursor-pointer bg-white w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tous les traitements</option>
                            {handledByList.map(name => (
                                <option key={name} value={name}>
                                    {name === 'none'
                                        ? 'Non traité'
                                        : name}
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                            <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={resetFilters}
                        className="bg-gray-200 px-3 py-2 rounded hover:bg-gray-300"
                    >
                        Réinitialiser les filtres
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto bg-white border rounded">
                <table className="min-w-full text-left">
                    <thead className="bg-gray-100">
                        <tr>
                            <th
                                onClick={() => sortBy('transactionNumber')}
                                className="p-2 cursor-pointer"
                            >
                                Dossier n°
                            </th>
                            <th
                                onClick={() => sortBy('lastname')}
                                className="p-2 cursor-pointer"
                            >
                                Nom
                            </th>
                            <th
                                onClick={() => sortBy('email')}
                                className="p-2 cursor-pointer"
                            >
                                Email
                            </th>
                            <th
                                onClick={() => sortBy('companyName')}
                                className="p-2 cursor-pointer"
                            >
                                Sous-domaine
                            </th>
                            <th
                                onClick={() => sortBy('createdAt')}
                                className="p-2 cursor-pointer"
                            >
                                Créé le
                            </th>
                            <th className="p-2">Services achetés</th>
                            <th className="p-2">Statut</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    colSpan="7"
                                    className="p-4 pt-24 text-center"
                                >
                                    <Loading />
                                </td>
                            </tr>
                        )}

                        {!loading &&
                            paginated.map(submission => {
                                const customer = submission?.customer || {};
                                const lastStatusChange = getLastHandler(
                                    submission
                                );

                                return (
                                    <tr
                                        key={submission._id}
                                        className="border-b hover:bg-gray-50 cursor-pointer"
                                        onClick={event => {
                                            if (
                                                !event.target.closest(
                                                    '.status-dropdown'
                                                ) &&
                                                !event.target.closest(
                                                    '.status-dropdown-portal'
                                                )
                                            ) {
                                                openSubmission(
                                                    submission._id
                                                );
                                            }
                                        }}
                                    >
                                        <td className="p-2">
                                            {submission.societyPrefix
                                                ? `${submission.societyPrefix}-`
                                                : ''}
                                            {submission.dossierNumber ||
                                                submission.transactionNumber ||
                                                '-'}
                                        </td>
                                        <td className="p-2">
                                            {customer.lastname || ''}{' '}
                                            {customer.firstname || ''}
                                        </td>
                                        <td className="p-2">
                                            {customer.email || '-'}
                                        </td>
                                        <td className="p-2 capitalize">
                                            {submission.subdomain || '-'}
                                        </td>
                                        <td className="p-2">
                                            {moment(
                                                submission.createdAt
                                            ).format('DD/MM/YYYY')}
                                        </td>
                                        <td className="p-2">
                                            <ul className="list-disc list-inside space-y-1">
                                                {(submission.services || []).map(
                                                    service => (
                                                        <li
                                                            key={
                                                                service._id ||
                                                                getServiceLabel(
                                                                    service
                                                                )
                                                            }
                                                        >
                                                            {getServiceLabel(
                                                                service
                                                            )}{' '}
                                                            —{' '}
                                                            {Number(
                                                                service.price ||
                                                                0
                                                            ).toFixed(2)}
                                                            €
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </td>
                                        <td
                                            className="p-2"
                                            onClick={event =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <div className="status-dropdown">
                                                <StatusDropdown
                                                    submissionId={
                                                        submission._id
                                                    }
                                                    currentStatus={
                                                        submission.status
                                                    }
                                                    onStatusChange={
                                                        updateStatus
                                                    }
                                                />

                                                {lastStatusChange && (
                                                    <div className="text-xs text-gray-500 italic mt-2">
                                                        Traité par :{' '}
                                                        {
                                                            lastStatusChange.firstName
                                                        }{' '}
                                                        {
                                                            lastStatusChange.lastName
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}

                        {!loading && paginated.length === 0 && (
                            <tr>
                                <td
                                    colSpan="7"
                                    className="p-4 text-center"
                                >
                                    Aucun résultat
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Summary */}
            <div className="flex justify-between items-center">
                <div>
                    Total: {filtered.length} ligne
                    {filtered.length > 1 ? 's' : ''}
                </div>

                <div className="flex items-center gap-2">
                    <label>Afficher </label>
                    <select
                        value={pageSize}
                        onChange={event => {
                            setPageSize(Number(event.target.value));
                            setPage(1);
                        }}
                        className="border bg-white rounded px-2 py-1"
                    >
                        {[10, 25, 50, 100].map(size => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                    <label> lignes / page</label>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4">
                <button
                    type="button"
                    onClick={() =>
                        setPage(currentPage =>
                            Math.max(1, currentPage - 1)
                        )
                    }
                    disabled={page === 1}
                    className={`p-2 rounded ${page === 1
                        ? 'bg-gray-200'
                        : 'bg-primary text-white'
                        }`}
                >
                    <FaChevronLeft />
                </button>

                <span>
                    Page {page} / {maxPage}
                </span>

                <button
                    type="button"
                    onClick={() =>
                        setPage(currentPage =>
                            Math.min(maxPage, currentPage + 1)
                        )
                    }
                    disabled={page === maxPage}
                    className={`p-2 rounded ${page === maxPage
                        ? 'bg-gray-200'
                        : 'bg-primary text-white'
                        }`}
                >
                    <FaChevronRight />
                </button>
            </div>
        </div>
    );
}

export default DashboardPage;